import { Locale } from "@/lib/i18n";

type EditorExtraCopy = {
  draw: string;
  eraser: string;
  image: string;
  sign: string;
  stamp: string;
  link: string;
  note: string;
  toggleThumbnails: string;
  undo: string;
  redo: string;
  zoomIn: string;
  zoomOut: string;
  save: string;
  saveOptions: string;
  saveAsPdf: string;
  saveAsJpg: string;
  saveAsPng: string;
  exportToExcel: string;
  exportToWord: string;
  noPdfLoaded: string;
  failedToSavePdf: string;
  redirectingToPdfToExcel: string;
  redirectingToPdfToWord: string;
  pleaseWaitForPdf: string;
  addLink: string;
  insertClickableLinkArea: string;
  destinationUrl: string;
  addWatermark: string;
  watermarkText: string;
  enterWatermarkText: string;
  fontSize: string;
  opacity: string;
  rotation: string;
  previewText: string;
  color: string;
  textColor: string;
  strokeWidth: string;
  alignLeft: string;
  alignCenter: string;
  alignRight: string;
  delete: string;
  bold: string;
  italic: string;
  underline: string;
  noPageLoaded: string;
  clickToEdit: string;
};

const en: EditorExtraCopy = {
  draw: "Draw",
  eraser: "Eraser",
  image: "Image",
  sign: "Sign",
  stamp: "Stamp",
  link: "Link",
  note: "Note",
  toggleThumbnails: "Toggle thumbnails",
  undo: "Undo",
  redo: "Redo",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  save: "Save",
  saveOptions: "Save options",
  saveAsPdf: "Save as PDF",
  saveAsJpg: "Save as JPG",
  saveAsPng: "Save as PNG",
  exportToExcel: "Export to Excel",
  exportToWord: "Export to Word",
  noPdfLoaded: "No PDF loaded or canvas not initialized.",
  failedToSavePdf: "Failed to save PDF. Please try again.",
  redirectingToPdfToExcel: "Redirecting to PDF to Excel converter...",
  redirectingToPdfToWord: "Redirecting to PDF to Word converter...",
  pleaseWaitForPdf: "Please wait for the PDF to finish loading before using tools.",
  addLink: "Add Link",
  insertClickableLinkArea: "Insert a clickable link area into your PDF document.",
  destinationUrl: "Destination URL",
  addWatermark: "Add Watermark",
  watermarkText: "Watermark Text",
  enterWatermarkText: "Enter watermark text...",
  fontSize: "Font Size",
  opacity: "Opacity",
  rotation: "Rotation",
  previewText: "Preview Text",
  color: "Color",
  textColor: "Text color",
  strokeWidth: "Stroke Width",
  alignLeft: "Align left",
  alignCenter: "Align center",
  alignRight: "Align right",
  delete: "Delete",
  bold: "Bold",
  italic: "Italic",
  underline: "Underline",
  noPageLoaded: "No page loaded",
  clickToEdit: "Click to edit",
};

const overrides: Partial<Record<Locale, Partial<EditorExtraCopy>>> = {
  id: {
    draw: "Gambar",
    eraser: "Penghapus",
    image: "Gambar",
    sign: "Tanda tangan",
    stamp: "Stempel",
    link: "Tautan",
    note: "Catatan",
    toggleThumbnails: "Tampilkan/sembunyikan thumbnail",
    undo: "Urungkan",
    redo: "Ulangi",
    zoomIn: "Perbesar",
    zoomOut: "Perkecil",
    save: "Simpan",
    saveOptions: "Opsi simpan",
    saveAsPdf: "Simpan sebagai PDF",
    saveAsJpg: "Simpan sebagai JPG",
    saveAsPng: "Simpan sebagai PNG",
    exportToExcel: "Ekspor ke Excel",
    exportToWord: "Ekspor ke Word",
    noPdfLoaded: "PDF belum dimuat atau canvas belum siap.",
    failedToSavePdf: "Gagal menyimpan PDF. Silakan coba lagi.",
    redirectingToPdfToExcel: "Mengalihkan ke konverter PDF ke Excel...",
    redirectingToPdfToWord: "Mengalihkan ke konverter PDF ke Word...",
    pleaseWaitForPdf: "Tunggu sampai PDF selesai dimuat sebelum memakai tools.",
    addLink: "Tambah Tautan",
    insertClickableLinkArea: "Masukkan area tautan yang bisa diklik ke dokumen PDF kamu.",
    destinationUrl: "URL Tujuan",
    addWatermark: "Tambah Watermark",
    watermarkText: "Teks Watermark",
    enterWatermarkText: "Masukkan teks watermark...",
    fontSize: "Ukuran Font",
    opacity: "Opasitas",
    rotation: "Rotasi",
    previewText: "Teks Preview",
    color: "Warna",
    textColor: "Warna teks",
    strokeWidth: "Ketebalan Garis",
    alignLeft: "Rata kiri",
    alignCenter: "Rata tengah",
    alignRight: "Rata kanan",
    delete: "Hapus",
    bold: "Tebal",
    italic: "Miring",
    underline: "Garis bawah",
    noPageLoaded: "Belum ada halaman dimuat",
    clickToEdit: "Klik untuk mengedit",
  },
  vi: {
    draw: "Vẽ",
    eraser: "Tẩy",
    image: "Hình ảnh",
    sign: "Ký tên",
    stamp: "Dấu",
    link: "Liên kết",
    note: "Ghi chú",
    toggleThumbnails: "Bật/tắt ảnh thu nhỏ",
    undo: "Hoàn tác",
    redo: "Làm lại",
    zoomIn: "Phóng to",
    zoomOut: "Thu nhỏ",
    save: "Lưu",
    saveOptions: "Tùy chọn lưu",
    saveAsPdf: "Lưu dưới dạng PDF",
    saveAsJpg: "Lưu dưới dạng JPG",
    saveAsPng: "Lưu dưới dạng PNG",
    exportToExcel: "Xuất sang Excel",
    exportToWord: "Xuất sang Word",
    noPdfLoaded: "Chưa tải PDF hoặc canvas chưa sẵn sàng.",
    failedToSavePdf: "Không thể lưu PDF. Vui lòng thử lại.",
    redirectingToPdfToExcel: "Đang chuyển đến công cụ PDF sang Excel...",
    redirectingToPdfToWord: "Đang chuyển đến công cụ PDF sang Word...",
    pleaseWaitForPdf: "Vui lòng đợi PDF tải xong trước khi dùng công cụ.",
    addLink: "Thêm liên kết",
    insertClickableLinkArea: "Chèn vùng liên kết có thể bấm vào tài liệu PDF của bạn.",
    destinationUrl: "URL đích",
    addWatermark: "Thêm watermark",
    watermarkText: "Văn bản watermark",
    enterWatermarkText: "Nhập văn bản watermark...",
    fontSize: "Cỡ chữ",
    opacity: "Độ mờ",
    rotation: "Xoay",
    previewText: "Văn bản xem trước",
    color: "Màu",
    textColor: "Màu chữ",
    strokeWidth: "Độ dày nét",
    alignLeft: "Căn trái",
    alignCenter: "Căn giữa",
    alignRight: "Căn phải",
    delete: "Xóa",
    bold: "Đậm",
    italic: "Nghiêng",
    underline: "Gạch chân",
    noPageLoaded: "Chưa tải trang nào",
    clickToEdit: "Bấm để chỉnh sửa",
  },
  cs: {
    draw: "Kreslit",
    eraser: "Guma",
    image: "Obrázek",
    sign: "Podepsat",
    stamp: "Razítko",
    link: "Odkaz",
    note: "Poznámka",
    toggleThumbnails: "Přepnout náhledy",
    undo: "Zpět",
    redo: "Znovu",
    zoomIn: "Přiblížit",
    zoomOut: "Oddálit",
    save: "Uložit",
    saveOptions: "Možnosti uložení",
    saveAsPdf: "Uložit jako PDF",
    saveAsJpg: "Uložit jako JPG",
    saveAsPng: "Uložit jako PNG",
    exportToExcel: "Export do Excelu",
    exportToWord: "Export do Wordu",
    noPdfLoaded: "PDF není načteno nebo plátno není připraveno.",
    failedToSavePdf: "Nepodařilo se uložit PDF. Zkuste to prosím znovu.",
    redirectingToPdfToExcel: "Přesměrování do převodníku PDF do Excelu...",
    redirectingToPdfToWord: "Přesměrování do převodníku PDF do Wordu...",
    pleaseWaitForPdf: "Před použitím nástrojů počkejte na dokončení načítání PDF.",
    addLink: "Přidat odkaz",
    insertClickableLinkArea: "Vložte do PDF klikací oblast odkazu.",
    destinationUrl: "Cílová URL",
    addWatermark: "Přidat vodoznak",
    watermarkText: "Text vodoznaku",
    enterWatermarkText: "Zadejte text vodoznaku...",
    fontSize: "Velikost písma",
    opacity: "Průhlednost",
    rotation: "Otočení",
    previewText: "Text náhledu",
    color: "Barva",
    textColor: "Barva textu",
    strokeWidth: "Tloušťka čáry",
    alignLeft: "Zarovnat vlevo",
    alignCenter: "Zarovnat na střed",
    alignRight: "Zarovnat vpravo",
    delete: "Smazat",
    bold: "Tučné",
    italic: "Kurzíva",
    underline: "Podtržení",
    noPageLoaded: "Nebyla načtena žádná stránka",
    clickToEdit: "Klikněte pro úpravu",
  },
  es: {
    draw: "Dibujar",
    eraser: "Borrador",
    image: "Imagen",
    sign: "Firmar",
    stamp: "Sello",
    link: "Enlace",
    note: "Nota",
    toggleThumbnails: "Mostrar u ocultar miniaturas",
    undo: "Deshacer",
    redo: "Rehacer",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    save: "Guardar",
    saveOptions: "Opciones de guardado",
    saveAsPdf: "Guardar como PDF",
    saveAsJpg: "Guardar como JPG",
    saveAsPng: "Guardar como PNG",
    exportToExcel: "Exportar a Excel",
    exportToWord: "Exportar a Word",
    noPdfLoaded: "No hay PDF cargado o el lienzo no está listo.",
    failedToSavePdf: "No se pudo guardar el PDF. Inténtalo de nuevo.",
    redirectingToPdfToExcel: "Redirigiendo al convertidor de PDF a Excel...",
    redirectingToPdfToWord: "Redirigiendo al convertidor de PDF a Word...",
    pleaseWaitForPdf: "Espera a que el PDF termine de cargar antes de usar las herramientas.",
    addLink: "Agregar enlace",
    insertClickableLinkArea: "Inserta un área de enlace clicable en tu documento PDF.",
    destinationUrl: "URL de destino",
    addWatermark: "Agregar marca de agua",
    watermarkText: "Texto de la marca de agua",
    enterWatermarkText: "Ingresa el texto de la marca de agua...",
    fontSize: "Tamaño de fuente",
    opacity: "Opacidad",
    rotation: "Rotación",
    previewText: "Texto de vista previa",
    color: "Color",
    textColor: "Color del texto",
    strokeWidth: "Grosor del trazo",
    alignLeft: "Alinear a la izquierda",
    alignCenter: "Alinear al centro",
    alignRight: "Alinear a la derecha",
    delete: "Eliminar",
    bold: "Negrita",
    italic: "Cursiva",
    underline: "Subrayado",
    noPageLoaded: "No hay página cargada",
    clickToEdit: "Haz clic para editar",
  },
  fr: {
    draw: "Dessiner",
    eraser: "Gomme",
    image: "Image",
    sign: "Signer",
    stamp: "Tampon",
    link: "Lien",
    note: "Note",
    toggleThumbnails: "Afficher ou masquer les miniatures",
    undo: "Annuler",
    redo: "Rétablir",
    zoomIn: "Zoom avant",
    zoomOut: "Zoom arrière",
    save: "Enregistrer",
    saveOptions: "Options d'enregistrement",
    saveAsPdf: "Enregistrer en PDF",
    saveAsJpg: "Enregistrer en JPG",
    saveAsPng: "Enregistrer en PNG",
    exportToExcel: "Exporter vers Excel",
    exportToWord: "Exporter vers Word",
    noPdfLoaded: "Aucun PDF chargé ou le canevas n'est pas prêt.",
    failedToSavePdf: "Impossible d'enregistrer le PDF. Veuillez réessayer.",
    redirectingToPdfToExcel: "Redirection vers le convertisseur PDF vers Excel...",
    redirectingToPdfToWord: "Redirection vers le convertisseur PDF vers Word...",
    pleaseWaitForPdf: "Veuillez attendre la fin du chargement du PDF avant d'utiliser les outils.",
    addLink: "Ajouter un lien",
    insertClickableLinkArea: "Insérez une zone de lien cliquable dans votre document PDF.",
    destinationUrl: "URL de destination",
    addWatermark: "Ajouter un filigrane",
    watermarkText: "Texte du filigrane",
    enterWatermarkText: "Saisissez le texte du filigrane...",
    fontSize: "Taille de police",
    opacity: "Opacité",
    rotation: "Rotation",
    previewText: "Texte d'aperçu",
    color: "Couleur",
    textColor: "Couleur du texte",
    strokeWidth: "Épaisseur du trait",
    alignLeft: "Aligner à gauche",
    alignCenter: "Aligner au centre",
    alignRight: "Aligner à droite",
    delete: "Supprimer",
    bold: "Gras",
    italic: "Italique",
    underline: "Souligné",
    noPageLoaded: "Aucune page chargée",
    clickToEdit: "Cliquez pour modifier",
  },
  de: {
    draw: "Zeichnen",
    eraser: "Radierer",
    image: "Bild",
    sign: "Signieren",
    stamp: "Stempel",
    link: "Link",
    note: "Notiz",
    toggleThumbnails: "Miniaturen ein-/ausblenden",
    undo: "Rückgängig",
    redo: "Wiederholen",
    zoomIn: "Vergrößern",
    zoomOut: "Verkleinern",
    save: "Speichern",
    saveOptions: "Speicheroptionen",
    saveAsPdf: "Als PDF speichern",
    saveAsJpg: "Als JPG speichern",
    saveAsPng: "Als PNG speichern",
    exportToExcel: "Nach Excel exportieren",
    exportToWord: "Nach Word exportieren",
    noPdfLoaded: "Kein PDF geladen oder Canvas nicht bereit.",
    failedToSavePdf: "PDF konnte nicht gespeichert werden. Bitte erneut versuchen.",
    redirectingToPdfToExcel: "Weiterleitung zum PDF-zu-Excel-Konverter...",
    redirectingToPdfToWord: "Weiterleitung zum PDF-zu-Word-Konverter...",
    pleaseWaitForPdf: "Bitte warte, bis das PDF vollständig geladen ist, bevor du die Werkzeuge nutzt.",
    addLink: "Link hinzufügen",
    insertClickableLinkArea: "Füge einen anklickbaren Linkbereich in dein PDF-Dokument ein.",
    destinationUrl: "Ziel-URL",
    addWatermark: "Wasserzeichen hinzufügen",
    watermarkText: "Wasserzeichentext",
    enterWatermarkText: "Wasserzeichentext eingeben...",
    fontSize: "Schriftgröße",
    opacity: "Deckkraft",
    rotation: "Drehung",
    previewText: "Vorschautext",
    color: "Farbe",
    textColor: "Textfarbe",
    strokeWidth: "Linienstärke",
    alignLeft: "Links ausrichten",
    alignCenter: "Zentrieren",
    alignRight: "Rechts ausrichten",
    delete: "Löschen",
    bold: "Fett",
    italic: "Kursiv",
    underline: "Unterstrichen",
    noPageLoaded: "Keine Seite geladen",
    clickToEdit: "Klicken zum Bearbeiten",
  },
  it: {
    draw: "Disegna",
    eraser: "Gomma",
    image: "Immagine",
    sign: "Firma",
    stamp: "Timbro",
    link: "Link",
    note: "Nota",
    toggleThumbnails: "Mostra o nascondi miniature",
    undo: "Annulla",
    redo: "Ripristina",
    zoomIn: "Ingrandisci",
    zoomOut: "Riduci",
    save: "Salva",
    saveOptions: "Opzioni di salvataggio",
    saveAsPdf: "Salva come PDF",
    saveAsJpg: "Salva come JPG",
    saveAsPng: "Salva come PNG",
    exportToExcel: "Esporta in Excel",
    exportToWord: "Esporta in Word",
    noPdfLoaded: "Nessun PDF caricato o canvas non inizializzato.",
    failedToSavePdf: "Impossibile salvare il PDF. Riprova.",
    redirectingToPdfToExcel: "Reindirizzamento al convertitore PDF in Excel...",
    redirectingToPdfToWord: "Reindirizzamento al convertitore PDF in Word...",
    pleaseWaitForPdf: "Attendi il completamento del caricamento del PDF prima di usare gli strumenti.",
    addLink: "Aggiungi link",
    insertClickableLinkArea: "Inserisci un'area cliccabile nel tuo documento PDF.",
    destinationUrl: "URL di destinazione",
    addWatermark: "Aggiungi watermark",
    watermarkText: "Testo watermark",
    enterWatermarkText: "Inserisci il testo del watermark...",
    fontSize: "Dimensione carattere",
    opacity: "Opacità",
    rotation: "Rotazione",
    previewText: "Testo di anteprima",
    color: "Colore",
    textColor: "Colore del testo",
    strokeWidth: "Spessore tratto",
    alignLeft: "Allinea a sinistra",
    alignCenter: "Allinea al centro",
    alignRight: "Allinea a destra",
    delete: "Elimina",
    bold: "Grassetto",
    italic: "Corsivo",
    underline: "Sottolineato",
    noPageLoaded: "Nessuna pagina caricata",
    clickToEdit: "Clicca per modificare",
  },
  hu: {
    draw: "Rajz",
    eraser: "Radír",
    image: "Kép",
    sign: "Aláírás",
    stamp: "Bélyegző",
    link: "Hivatkozás",
    note: "Jegyzet",
    toggleThumbnails: "Bélyegképek megjelenítése/elrejtése",
    undo: "Visszavonás",
    redo: "Újra",
    zoomIn: "Nagyítás",
    zoomOut: "Kicsinyítés",
    save: "Mentés",
    saveOptions: "Mentési beállítások",
    saveAsPdf: "Mentés PDF-ként",
    saveAsJpg: "Mentés JPG-ként",
    saveAsPng: "Mentés PNG-ként",
    exportToExcel: "Exportálás Excelbe",
    exportToWord: "Exportálás Wordbe",
    noPdfLoaded: "Nincs betöltve PDF vagy a vászon nem áll készen.",
    failedToSavePdf: "Nem sikerült elmenteni a PDF-et. Próbáld újra.",
    redirectingToPdfToExcel: "Átirányítás a PDF-ből Excel konverterhez...",
    redirectingToPdfToWord: "Átirányítás a PDF-ből Word konverterhez...",
    pleaseWaitForPdf: "Kérlek, várd meg a PDF teljes betöltését a használat előtt.",
    addLink: "Hivatkozás hozzáadása",
    insertClickableLinkArea: "Szúrj be kattintható hivatkozási területet a PDF-dokumentumba.",
    destinationUrl: "Cél URL",
    addWatermark: "Vízjel hozzáadása",
    watermarkText: "Vízjel szövege",
    enterWatermarkText: "Add meg a vízjel szövegét...",
    fontSize: "Betűméret",
    opacity: "Átlátszóság",
    rotation: "Forgatás",
    previewText: "Előnézeti szöveg",
    color: "Szín",
    textColor: "Szövegszín",
    strokeWidth: "Vonalvastagság",
    alignLeft: "Balra igazítás",
    alignCenter: "Középre igazítás",
    alignRight: "Jobbra igazítás",
    delete: "Törlés",
    bold: "Félkövér",
    italic: "Dőlt",
    underline: "Aláhúzott",
    noPageLoaded: "Nincs betöltött oldal",
    clickToEdit: "Kattints a szerkesztéshez",
  },
  ms: {
    draw: "Lukis",
    eraser: "Pemadam",
    image: "Imej",
    sign: "Tandatangan",
    stamp: "Cop",
    link: "Pautan",
    note: "Nota",
    toggleThumbnails: "Tunjuk atau sorok lakaran kecil",
    undo: "Batal",
    redo: "Buat semula",
    zoomIn: "Zum masuk",
    zoomOut: "Zum keluar",
    save: "Simpan",
    saveOptions: "Pilihan simpan",
    saveAsPdf: "Simpan sebagai PDF",
    saveAsJpg: "Simpan sebagai JPG",
    saveAsPng: "Simpan sebagai PNG",
    exportToExcel: "Eksport ke Excel",
    exportToWord: "Eksport ke Word",
    noPdfLoaded: "Tiada PDF dimuatkan atau kanvas belum sedia.",
    failedToSavePdf: "Gagal menyimpan PDF. Sila cuba lagi.",
    redirectingToPdfToExcel: "Mengalih ke penukar PDF ke Excel...",
    redirectingToPdfToWord: "Mengalih ke penukar PDF ke Word...",
    pleaseWaitForPdf: "Sila tunggu sehingga PDF selesai dimuatkan sebelum menggunakan alat.",
    addLink: "Tambah pautan",
    insertClickableLinkArea: "Masukkan kawasan pautan boleh klik ke dalam dokumen PDF anda.",
    destinationUrl: "URL destinasi",
    addWatermark: "Tambah tanda air",
    watermarkText: "Teks tanda air",
    enterWatermarkText: "Masukkan teks tanda air...",
    fontSize: "Saiz fon",
    opacity: "Kelegapan",
    rotation: "Putaran",
    previewText: "Teks pratonton",
    color: "Warna",
    textColor: "Warna teks",
    strokeWidth: "Ketebalan garisan",
    alignLeft: "Jajar kiri",
    alignCenter: "Jajar tengah",
    alignRight: "Jajar kanan",
    delete: "Padam",
    bold: "Tebal",
    italic: "Condong",
    underline: "Garis bawah",
    noPageLoaded: "Tiada halaman dimuatkan",
    clickToEdit: "Klik untuk edit",
  },
  nl: {
    draw: "Tekenen",
    eraser: "Gum",
    image: "Afbeelding",
    sign: "Ondertekenen",
    stamp: "Stempel",
    link: "Link",
    note: "Notitie",
    toggleThumbnails: "Miniaturen tonen of verbergen",
    undo: "Ongedaan maken",
    redo: "Opnieuw",
    zoomIn: "Inzoomen",
    zoomOut: "Uitzoomen",
    save: "Opslaan",
    saveOptions: "Opslagopties",
    saveAsPdf: "Opslaan als PDF",
    saveAsJpg: "Opslaan als JPG",
    saveAsPng: "Opslaan als PNG",
    exportToExcel: "Exporteren naar Excel",
    exportToWord: "Exporteren naar Word",
    noPdfLoaded: "Geen PDF geladen of canvas niet klaar.",
    failedToSavePdf: "PDF opslaan mislukt. Probeer het opnieuw.",
    redirectingToPdfToExcel: "Doorsturen naar PDF-naar-Excel-converter...",
    redirectingToPdfToWord: "Doorsturen naar PDF-naar-Word-converter...",
    pleaseWaitForPdf: "Wacht tot de PDF volledig geladen is voordat je tools gebruikt.",
    addLink: "Link toevoegen",
    insertClickableLinkArea: "Voeg een klikbaar linkgebied toe aan je PDF-document.",
    destinationUrl: "Doel-URL",
    addWatermark: "Watermerk toevoegen",
    watermarkText: "Watermerktekst",
    enterWatermarkText: "Voer watermerktekst in...",
    fontSize: "Lettergrootte",
    opacity: "Dekking",
    rotation: "Rotatie",
    previewText: "Voorbeeldtekst",
    color: "Kleur",
    textColor: "Tekstkleur",
    strokeWidth: "Lijndikte",
    alignLeft: "Links uitlijnen",
    alignCenter: "Centreren",
    alignRight: "Rechts uitlijnen",
    delete: "Verwijderen",
    bold: "Vet",
    italic: "Cursief",
    underline: "Onderstrepen",
    noPageLoaded: "Geen pagina geladen",
    clickToEdit: "Klik om te bewerken",
  },
  pl: {
    draw: "Rysuj",
    eraser: "Gumka",
    image: "Obraz",
    sign: "Podpis",
    stamp: "Pieczątka",
    link: "Link",
    note: "Notatka",
    toggleThumbnails: "Pokaż lub ukryj miniatury",
    undo: "Cofnij",
    redo: "Ponów",
    zoomIn: "Powiększ",
    zoomOut: "Pomniejsz",
    save: "Zapisz",
    saveOptions: "Opcje zapisu",
    saveAsPdf: "Zapisz jako PDF",
    saveAsJpg: "Zapisz jako JPG",
    saveAsPng: "Zapisz jako PNG",
    exportToExcel: "Eksportuj do Excela",
    exportToWord: "Eksportuj do Worda",
    noPdfLoaded: "Brak załadowanego PDF lub kanwa nie jest gotowa.",
    failedToSavePdf: "Nie udało się zapisać PDF. Spróbuj ponownie.",
    redirectingToPdfToExcel: "Przekierowanie do konwertera PDF do Excela...",
    redirectingToPdfToWord: "Przekierowanie do konwertera PDF do Worda...",
    pleaseWaitForPdf: "Poczekaj, aż PDF w pełni się załaduje przed użyciem narzędzi.",
    addLink: "Dodaj link",
    insertClickableLinkArea: "Wstaw klikalny obszar linku do dokumentu PDF.",
    destinationUrl: "Docelowy URL",
    addWatermark: "Dodaj znak wodny",
    watermarkText: "Tekst znaku wodnego",
    enterWatermarkText: "Wpisz tekst znaku wodnego...",
    fontSize: "Rozmiar czcionki",
    opacity: "Przezroczystość",
    rotation: "Obrót",
    previewText: "Tekst podglądu",
    color: "Kolor",
    textColor: "Kolor tekstu",
    strokeWidth: "Grubość linii",
    alignLeft: "Wyrównaj do lewej",
    alignCenter: "Wyśrodkuj",
    alignRight: "Wyrównaj do prawej",
    delete: "Usuń",
    bold: "Pogrubienie",
    italic: "Kursywa",
    underline: "Podkreślenie",
    noPageLoaded: "Nie załadowano strony",
    clickToEdit: "Kliknij, aby edytować",
  },
  pt: {
    draw: "Desenhar",
    eraser: "Borracha",
    image: "Imagem",
    sign: "Assinar",
    stamp: "Carimbo",
    link: "Link",
    note: "Nota",
    toggleThumbnails: "Mostrar ou ocultar miniaturas",
    undo: "Desfazer",
    redo: "Refazer",
    zoomIn: "Ampliar",
    zoomOut: "Reduzir",
    save: "Salvar",
    saveOptions: "Opções de salvamento",
    saveAsPdf: "Salvar como PDF",
    saveAsJpg: "Salvar como JPG",
    saveAsPng: "Salvar como PNG",
    exportToExcel: "Exportar para Excel",
    exportToWord: "Exportar para Word",
    noPdfLoaded: "Nenhum PDF carregado ou canvas não inicializado.",
    failedToSavePdf: "Falha ao salvar o PDF. Tente novamente.",
    redirectingToPdfToExcel: "Redirecionando para o conversor de PDF para Excel...",
    redirectingToPdfToWord: "Redirecionando para o conversor de PDF para Word...",
    pleaseWaitForPdf: "Aguarde o PDF terminar de carregar antes de usar as ferramentas.",
    addLink: "Adicionar link",
    insertClickableLinkArea: "Insira uma área de link clicável no seu documento PDF.",
    destinationUrl: "URL de destino",
    addWatermark: "Adicionar marca d'água",
    watermarkText: "Texto da marca d'água",
    enterWatermarkText: "Digite o texto da marca d'água...",
    fontSize: "Tamanho da fonte",
    opacity: "Opacidade",
    rotation: "Rotação",
    previewText: "Texto de visualização",
    color: "Cor",
    textColor: "Cor do texto",
    strokeWidth: "Espessura do traço",
    alignLeft: "Alinhar à esquerda",
    alignCenter: "Centralizar",
    alignRight: "Alinhar à direita",
    delete: "Excluir",
    bold: "Negrito",
    italic: "Itálico",
    underline: "Sublinhado",
    noPageLoaded: "Nenhuma página carregada",
    clickToEdit: "Clique para editar",
  },
  th: {
    draw: "วาด",
    eraser: "ยางลบ",
    image: "รูปภาพ",
    sign: "ลงชื่อ",
    stamp: "ตราประทับ",
    link: "ลิงก์",
    note: "โน้ต",
    toggleThumbnails: "แสดงหรือซ่อนภาพย่อ",
    undo: "เลิกทำ",
    redo: "ทำซ้ำ",
    zoomIn: "ซูมเข้า",
    zoomOut: "ซูมออก",
    save: "บันทึก",
    saveOptions: "ตัวเลือกการบันทึก",
    saveAsPdf: "บันทึกเป็น PDF",
    saveAsJpg: "บันทึกเป็น JPG",
    saveAsPng: "บันทึกเป็น PNG",
    exportToExcel: "ส่งออกเป็น Excel",
    exportToWord: "ส่งออกเป็น Word",
    noPdfLoaded: "ยังไม่ได้โหลด PDF หรือแคนวาสยังไม่พร้อม",
    failedToSavePdf: "บันทึก PDF ไม่สำเร็จ กรุณาลองอีกครั้ง",
    redirectingToPdfToExcel: "กำลังไปยังตัวแปลง PDF เป็น Excel...",
    redirectingToPdfToWord: "กำลังไปยังตัวแปลง PDF เป็น Word...",
    pleaseWaitForPdf: "โปรดรอให้ PDF โหลดเสร็จก่อนใช้งานเครื่องมือ",
    addLink: "เพิ่มลิงก์",
    insertClickableLinkArea: "แทรกพื้นที่ลิงก์ที่คลิกได้ลงในเอกสาร PDF ของคุณ",
    destinationUrl: "URL ปลายทาง",
    addWatermark: "เพิ่มลายน้ำ",
    watermarkText: "ข้อความลายน้ำ",
    enterWatermarkText: "ป้อนข้อความลายน้ำ...",
    fontSize: "ขนาดตัวอักษร",
    opacity: "ความทึบ",
    rotation: "การหมุน",
    previewText: "ข้อความตัวอย่าง",
    color: "สี",
    textColor: "สีข้อความ",
    strokeWidth: "ความหนาเส้น",
    alignLeft: "จัดชิดซ้าย",
    alignCenter: "จัดกึ่งกลาง",
    alignRight: "จัดชิดขวา",
    delete: "ลบ",
    bold: "ตัวหนา",
    italic: "ตัวเอียง",
    underline: "ขีดเส้นใต้",
    noPageLoaded: "ยังไม่มีหน้าที่โหลด",
    clickToEdit: "คลิกเพื่อแก้ไข",
  },
  tr: {
    draw: "Çiz",
    eraser: "Silgi",
    image: "Görsel",
    sign: "İmzala",
    stamp: "Damga",
    link: "Bağlantı",
    note: "Not",
    toggleThumbnails: "Küçük görselleri göster/gizle",
    undo: "Geri al",
    redo: "Yinele",
    zoomIn: "Yakınlaştır",
    zoomOut: "Uzaklaştır",
    save: "Kaydet",
    saveOptions: "Kaydetme seçenekleri",
    saveAsPdf: "PDF olarak kaydet",
    saveAsJpg: "JPG olarak kaydet",
    saveAsPng: "PNG olarak kaydet",
    exportToExcel: "Excel'e aktar",
    exportToWord: "Word'e aktar",
    noPdfLoaded: "PDF yüklenmedi veya tuval hazır değil.",
    failedToSavePdf: "PDF kaydedilemedi. Lütfen tekrar deneyin.",
    redirectingToPdfToExcel: "PDF'den Excel'e dönüştürücüye yönlendiriliyor...",
    redirectingToPdfToWord: "PDF'den Word'e dönüştürücüye yönlendiriliyor...",
    pleaseWaitForPdf: "Araçları kullanmadan önce PDF'nin tamamen yüklenmesini bekleyin.",
    addLink: "Bağlantı ekle",
    insertClickableLinkArea: "PDF belgenize tıklanabilir bir bağlantı alanı ekleyin.",
    destinationUrl: "Hedef URL",
    addWatermark: "Filigran ekle",
    watermarkText: "Filigran metni",
    enterWatermarkText: "Filigran metnini girin...",
    fontSize: "Yazı boyutu",
    opacity: "Opaklık",
    rotation: "Döndürme",
    previewText: "Önizleme metni",
    color: "Renk",
    textColor: "Metin rengi",
    strokeWidth: "Çizgi kalınlığı",
    alignLeft: "Sola hizala",
    alignCenter: "Ortala",
    alignRight: "Sağa hizala",
    delete: "Sil",
    bold: "Kalın",
    italic: "İtalik",
    underline: "Altı çizili",
    noPageLoaded: "Yüklü sayfa yok",
    clickToEdit: "Düzenlemek için tıkla",
  },
  el: {
    draw: "Σχεδίαση",
    eraser: "Γόμα",
    image: "Εικόνα",
    sign: "Υπογραφή",
    stamp: "Σφραγίδα",
    link: "Σύνδεσμος",
    note: "Σημείωση",
    toggleThumbnails: "Εμφάνιση ή απόκρυψη μικρογραφιών",
    undo: "Αναίρεση",
    redo: "Επανάληψη",
    zoomIn: "Μεγέθυνση",
    zoomOut: "Σμίκρυνση",
    save: "Αποθήκευση",
    saveOptions: "Επιλογές αποθήκευσης",
    saveAsPdf: "Αποθήκευση ως PDF",
    saveAsJpg: "Αποθήκευση ως JPG",
    saveAsPng: "Αποθήκευση ως PNG",
    exportToExcel: "Εξαγωγή σε Excel",
    exportToWord: "Εξαγωγή σε Word",
    noPdfLoaded: "Δεν έχει φορτωθεί PDF ή ο καμβάς δεν είναι έτοιμος.",
    failedToSavePdf: "Η αποθήκευση του PDF απέτυχε. Δοκιμάστε ξανά.",
    redirectingToPdfToExcel: "Ανακατεύθυνση στον μετατροπέα PDF σε Excel...",
    redirectingToPdfToWord: "Ανακατεύθυνση στον μετατροπέα PDF σε Word...",
    pleaseWaitForPdf: "Περιμένετε να ολοκληρωθεί η φόρτωση του PDF πριν χρησιμοποιήσετε τα εργαλεία.",
    addLink: "Προσθήκη συνδέσμου",
    insertClickableLinkArea: "Εισαγάγετε μια clickable περιοχή συνδέσμου στο έγγραφο PDF σας.",
    destinationUrl: "URL προορισμού",
    addWatermark: "Προσθήκη υδατογραφήματος",
    watermarkText: "Κείμενο υδατογραφήματος",
    enterWatermarkText: "Εισαγάγετε κείμενο υδατογραφήματος...",
    fontSize: "Μέγεθος γραμματοσειράς",
    opacity: "Αδιαφάνεια",
    rotation: "Περιστροφή",
    previewText: "Κείμενο προεπισκόπησης",
    color: "Χρώμα",
    textColor: "Χρώμα κειμένου",
    strokeWidth: "Πάχος γραμμής",
    alignLeft: "Στοίχιση αριστερά",
    alignCenter: "Στοίχιση στο κέντρο",
    alignRight: "Στοίχιση δεξιά",
    delete: "Διαγραφή",
    bold: "Έντονα",
    italic: "Πλάγια",
    underline: "Υπογράμμιση",
    noPageLoaded: "Δεν έχει φορτωθεί σελίδα",
    clickToEdit: "Κάντε κλικ για επεξεργασία",
  },
  uk: {
    draw: "Малювати",
    eraser: "Гумка",
    image: "Зображення",
    sign: "Підпис",
    stamp: "Штамп",
    link: "Посилання",
    note: "Нотатка",
    toggleThumbnails: "Показати або сховати мініатюри",
    undo: "Скасувати",
    redo: "Повторити",
    zoomIn: "Збільшити",
    zoomOut: "Зменшити",
    save: "Зберегти",
    saveOptions: "Параметри збереження",
    saveAsPdf: "Зберегти як PDF",
    saveAsJpg: "Зберегти як JPG",
    saveAsPng: "Зберегти як PNG",
    exportToExcel: "Експортувати в Excel",
    exportToWord: "Експортувати в Word",
    noPdfLoaded: "PDF не завантажено або полотно не готове.",
    failedToSavePdf: "Не вдалося зберегти PDF. Спробуйте ще раз.",
    redirectingToPdfToExcel: "Перенаправлення до конвертера PDF в Excel...",
    redirectingToPdfToWord: "Перенаправлення до конвертера PDF у Word...",
    pleaseWaitForPdf: "Будь ласка, дочекайтеся повного завантаження PDF перед використанням інструментів.",
    addLink: "Додати посилання",
    insertClickableLinkArea: "Вставте клікабельну область посилання у ваш PDF-документ.",
    destinationUrl: "Цільова URL-адреса",
    addWatermark: "Додати водяний знак",
    watermarkText: "Текст водяного знака",
    enterWatermarkText: "Введіть текст водяного знака...",
    fontSize: "Розмір шрифту",
    opacity: "Прозорість",
    rotation: "Поворот",
    previewText: "Текст попереднього перегляду",
    color: "Колір",
    textColor: "Колір тексту",
    strokeWidth: "Товщина лінії",
    alignLeft: "Вирівняти ліворуч",
    alignCenter: "Вирівняти по центру",
    alignRight: "Вирівняти праворуч",
    delete: "Видалити",
    bold: "Жирний",
    italic: "Курсив",
    underline: "Підкреслення",
    noPageLoaded: "Сторінку не завантажено",
    clickToEdit: "Натисніть для редагування",
  },
  ru: {
    draw: "Рисовать",
    eraser: "Ластик",
    image: "Изображение",
    sign: "Подписать",
    stamp: "Штамп",
    link: "Ссылка",
    note: "Заметка",
    toggleThumbnails: "Показать или скрыть миниатюры",
    undo: "Отменить",
    redo: "Повторить",
    zoomIn: "Увеличить",
    zoomOut: "Уменьшить",
    save: "Сохранить",
    saveOptions: "Параметры сохранения",
    saveAsPdf: "Сохранить как PDF",
    saveAsJpg: "Сохранить как JPG",
    saveAsPng: "Сохранить как PNG",
    exportToExcel: "Экспорт в Excel",
    exportToWord: "Экспорт в Word",
    noPdfLoaded: "PDF не загружен или холст не инициализирован.",
    failedToSavePdf: "Не удалось сохранить PDF. Попробуйте еще раз.",
    redirectingToPdfToExcel: "Переход к конвертеру PDF в Excel...",
    redirectingToPdfToWord: "Переход к конвертеру PDF в Word...",
    pleaseWaitForPdf: "Пожалуйста, дождитесь полной загрузки PDF перед использованием инструментов.",
    addLink: "Добавить ссылку",
    insertClickableLinkArea: "Вставьте кликабельную область ссылки в ваш PDF-документ.",
    destinationUrl: "Целевой URL",
    addWatermark: "Добавить водяной знак",
    watermarkText: "Текст водяного знака",
    enterWatermarkText: "Введите текст водяного знака...",
    fontSize: "Размер шрифта",
    opacity: "Непрозрачность",
    rotation: "Поворот",
    previewText: "Текст предпросмотра",
    color: "Цвет",
    textColor: "Цвет текста",
    strokeWidth: "Толщина линии",
    alignLeft: "Выровнять по левому краю",
    alignCenter: "Выровнять по центру",
    alignRight: "Выровнять по правому краю",
    delete: "Удалить",
    bold: "Жирный",
    italic: "Курсив",
    underline: "Подчеркивание",
    noPageLoaded: "Страница не загружена",
    clickToEdit: "Нажмите для редактирования",
  },
  ar: {
    draw: "رسم",
    eraser: "ممحاة",
    image: "صورة",
    sign: "توقيع",
    stamp: "ختم",
    link: "رابط",
    note: "ملاحظة",
    toggleThumbnails: "إظهار أو إخفاء الصور المصغرة",
    undo: "تراجع",
    redo: "إعادة",
    zoomIn: "تكبير",
    zoomOut: "تصغير",
    save: "حفظ",
    saveOptions: "خيارات الحفظ",
    saveAsPdf: "حفظ كملف PDF",
    saveAsJpg: "حفظ كملف JPG",
    saveAsPng: "حفظ كملف PNG",
    exportToExcel: "تصدير إلى Excel",
    exportToWord: "تصدير إلى Word",
    noPdfLoaded: "لم يتم تحميل PDF أو لم يتم تهيئة اللوحة.",
    failedToSavePdf: "فشل حفظ ملف PDF. يرجى المحاولة مرة أخرى.",
    redirectingToPdfToExcel: "جارٍ التحويل إلى أداة PDF إلى Excel...",
    redirectingToPdfToWord: "جارٍ التحويل إلى أداة PDF إلى Word...",
    pleaseWaitForPdf: "يرجى الانتظار حتى يكتمل تحميل ملف PDF قبل استخدام الأدوات.",
    addLink: "إضافة رابط",
    insertClickableLinkArea: "أدرج منطقة رابط قابلة للنقر داخل مستند PDF الخاص بك.",
    destinationUrl: "رابط الوجهة",
    addWatermark: "إضافة علامة مائية",
    watermarkText: "نص العلامة المائية",
    enterWatermarkText: "أدخل نص العلامة المائية...",
    fontSize: "حجم الخط",
    opacity: "الشفافية",
    rotation: "الدوران",
    previewText: "نص المعاينة",
    color: "اللون",
    textColor: "لون النص",
    strokeWidth: "سماكة الخط",
    alignLeft: "محاذاة لليسار",
    alignCenter: "محاذاة للوسط",
    alignRight: "محاذاة لليمين",
    delete: "حذف",
    bold: "عريض",
    italic: "مائل",
    underline: "تحته خط",
    noPageLoaded: "لم يتم تحميل أي صفحة",
    clickToEdit: "انقر للتعديل",
  },
  ko: {
    draw: "그리기",
    eraser: "지우개",
    image: "이미지",
    sign: "서명",
    stamp: "도장",
    link: "링크",
    note: "메모",
    toggleThumbnails: "썸네일 표시 또는 숨기기",
    undo: "실행 취소",
    redo: "다시 실행",
    zoomIn: "확대",
    zoomOut: "축소",
    save: "저장",
    saveOptions: "저장 옵션",
    saveAsPdf: "PDF로 저장",
    saveAsJpg: "JPG로 저장",
    saveAsPng: "PNG로 저장",
    exportToExcel: "Excel로 내보내기",
    exportToWord: "Word로 내보내기",
    noPdfLoaded: "PDF가 로드되지 않았거나 캔버스가 준비되지 않았습니다.",
    failedToSavePdf: "PDF 저장에 실패했습니다. 다시 시도해 주세요.",
    redirectingToPdfToExcel: "PDF를 Excel로 변환하는 도구로 이동 중...",
    redirectingToPdfToWord: "PDF를 Word로 변환하는 도구로 이동 중...",
    pleaseWaitForPdf: "도구를 사용하기 전에 PDF 로딩이 완료될 때까지 기다려 주세요.",
    addLink: "링크 추가",
    insertClickableLinkArea: "PDF 문서에 클릭 가능한 링크 영역을 삽입하세요.",
    destinationUrl: "대상 URL",
    addWatermark: "워터마크 추가",
    watermarkText: "워터마크 텍스트",
    enterWatermarkText: "워터마크 텍스트를 입력하세요...",
    fontSize: "글꼴 크기",
    opacity: "불투명도",
    rotation: "회전",
    previewText: "미리보기 텍스트",
    color: "색상",
    textColor: "텍스트 색상",
    strokeWidth: "선 두께",
    alignLeft: "왼쪽 정렬",
    alignCenter: "가운데 정렬",
    alignRight: "오른쪽 정렬",
    delete: "삭제",
    bold: "굵게",
    italic: "기울임꼴",
    underline: "밑줄",
    noPageLoaded: "로드된 페이지가 없습니다",
    clickToEdit: "클릭하여 편집",
  },
  ja: {
    draw: "描画",
    eraser: "消しゴム",
    image: "画像",
    sign: "署名",
    stamp: "スタンプ",
    link: "リンク",
    note: "メモ",
    toggleThumbnails: "サムネイルを表示または非表示",
    undo: "元に戻す",
    redo: "やり直し",
    zoomIn: "拡大",
    zoomOut: "縮小",
    save: "保存",
    saveOptions: "保存オプション",
    saveAsPdf: "PDFとして保存",
    saveAsJpg: "JPGとして保存",
    saveAsPng: "PNGとして保存",
    exportToExcel: "Excelに書き出す",
    exportToWord: "Wordに書き出す",
    noPdfLoaded: "PDFが読み込まれていないか、キャンバスが準備できていません。",
    failedToSavePdf: "PDFの保存に失敗しました。もう一度お試しください。",
    redirectingToPdfToExcel: "PDF から Excel への変換ツールに移動しています...",
    redirectingToPdfToWord: "PDF から Word への変換ツールに移動しています...",
    pleaseWaitForPdf: "ツールを使う前に PDF の読み込みが完了するまでお待ちください。",
    addLink: "リンクを追加",
    insertClickableLinkArea: "PDF 文書にクリック可能なリンク領域を挿入します。",
    destinationUrl: "リンク先 URL",
    addWatermark: "透かしを追加",
    watermarkText: "透かしテキスト",
    enterWatermarkText: "透かしテキストを入力してください...",
    fontSize: "フォントサイズ",
    opacity: "不透明度",
    rotation: "回転",
    previewText: "プレビューテキスト",
    color: "色",
    textColor: "文字色",
    strokeWidth: "線の太さ",
    alignLeft: "左揃え",
    alignCenter: "中央揃え",
    alignRight: "右揃え",
    delete: "削除",
    bold: "太字",
    italic: "斜体",
    underline: "下線",
    noPageLoaded: "ページが読み込まれていません",
    clickToEdit: "クリックして編集",
  },
  ro: {
    draw: "Desenează",
    eraser: "Radieră",
    image: "Imagine",
    sign: "Semnează",
    stamp: "Ștampilă",
    link: "Link",
    note: "Notă",
    toggleThumbnails: "Afișează sau ascunde miniaturile",
    undo: "Anulează",
    redo: "Refă",
    zoomIn: "Mărește",
    zoomOut: "Micșorează",
    save: "Salvează",
    saveOptions: "Opțiuni de salvare",
    saveAsPdf: "Salvează ca PDF",
    saveAsJpg: "Salvează ca JPG",
    saveAsPng: "Salvează ca PNG",
    exportToExcel: "Exportă în Excel",
    exportToWord: "Exportă în Word",
    noPdfLoaded: "Nu există PDF încărcat sau canvas-ul nu este pregătit.",
    failedToSavePdf: "Salvarea PDF-ului a eșuat. Încearcă din nou.",
    redirectingToPdfToExcel: "Redirecționare către convertorul PDF în Excel...",
    redirectingToPdfToWord: "Redirecționare către convertorul PDF în Word...",
    pleaseWaitForPdf: "Așteaptă finalizarea încărcării PDF-ului înainte de a folosi instrumentele.",
    addLink: "Adaugă link",
    insertClickableLinkArea: "Inserează o zonă de link pe care se poate face clic în documentul tău PDF.",
    destinationUrl: "URL destinație",
    addWatermark: "Adaugă watermark",
    watermarkText: "Text watermark",
    enterWatermarkText: "Introdu textul watermark-ului...",
    fontSize: "Dimensiune font",
    opacity: "Opacitate",
    rotation: "Rotire",
    previewText: "Text de previzualizare",
    color: "Culoare",
    textColor: "Culoarea textului",
    strokeWidth: "Grosimea liniei",
    alignLeft: "Aliniază la stânga",
    alignCenter: "Centrează",
    alignRight: "Aliniază la dreapta",
    delete: "Șterge",
    bold: "Aldin",
    italic: "Cursiv",
    underline: "Subliniat",
    noPageLoaded: "Nicio pagină încărcată",
    clickToEdit: "Apasă pentru editare",
  },
};

export function getEditorExtraCopy(locale: Locale): EditorExtraCopy {
  return { ...en, ...(overrides[locale] ?? {}) };
}
